<template>
  <div class="iq-card">
    <div class="iq-card-body">
      <div class="d-flex align-items-center">
        <div class="w-100">
          <h5 class="mb-2 chart-title text-secondary">{{ title }}</h5>
          <div class="d-flex justify-content-between align-items-center">
            <h3 class="counter">{{ value }}<span class="counter-unit">{{ displayUnit }}</span></h3>
          </div>
        </div>
      </div>
      <apexchart :height="chartOptions.chart.height" :type="chartOptions.chart.type" :options="chartOptions" :series="series" />
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { formatDateTime } from '@/util/date';

export default {
  name: 'TemperatureChart',
  components: {
    apexchart: VueApexCharts,
  },
  props: {
    summaryData: { type: Object, required: true },
    mappedSeries: { type: Array, required: true },
  },
  computed: {
    title() { return 'Temperature'; },
    displayUnit() { return '°C'; },
    value() {
      const avg = this.summaryData?.flowmeter?.summary?.BUNKER_IN?._averageTemp;
      return avg ? avg.toFixed(2) : '0.00';
    },
    series() {
      const seriesData = this.mappedSeries.length > 60 ? this.mappedSeries.slice(-60) : this.mappedSeries;

      const mappedData = seriesData.map((d) => {
        const rawValue = d.fmData?.BUNKER_IN?.temperature;
        if (rawValue == null || !Number.isFinite(Number(rawValue))) {
          return null;
        }
        return Number(Number(rawValue).toFixed(2));
      });

      const interpolatedData = this.interpolateNulls(mappedData);

      return [
        { name: this.title, data: mappedData },
        { name: `${this.title} (Interpolated)`, data: interpolatedData },
      ];
    },
    chartOptions() {
      const seriesData = this.mappedSeries.length > 60 ? this.mappedSeries.slice(-60) : this.mappedSeries;
      const categories = seriesData.map((d) => formatDateTime(d.timestamp * 1000, 'DD MMMM YYYY HH:mm', null, false));

      return {
        chart: {
          height: 80,
          type: 'area',
          sparkline: { enabled: true },
          group: 'sparklines',
          animations: {
            enabled: false, // <--- ANIMASI DIMATIKAN
          },
        },
        dataLabels: { enabled: false },
        stroke: {
          width: 3,
          curve: 'smooth',
          dashArray: [0, 4],
        },
        fill: {
          type: 'gradient',
          gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0 },
        },
        colors: ['#B141E6', 'rgba(177, 65, 230, 0.4)'],
        xaxis: {
          type: 'datetime',
          categories,
        },
        yaxis: { min: 0, max: 50 },
        tooltip: {
          enabled: true,
          custom({
            series, seriesIndex, dataPointIndex, w,
          }) {
            if (seriesIndex === 1 || series[seriesIndex][dataPointIndex] === null) {
              return '';
            }
            const seriesName = w.globals.seriesNames[seriesIndex];
            const value = series[seriesIndex][dataPointIndex];
            const color = w.globals.colors[seriesIndex];
            const category = w.config.xaxis.categories[dataPointIndex];
            const title = formatDateTime(category, 'DD/MM/YY HH:mm', 'DD MMMM YYYY HH:mm');

            return `<div class="apexcharts-tooltip apexcharts-theme-light" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;"><div class="apexcharts-tooltip-title" style="padding: 6px 10px;">${title}</div><div class="apexcharts-tooltip-series-group apexcharts-active" style="padding: 6px 10px; display: flex; align-items: center;"><span class="apexcharts-tooltip-marker" style="background-color: ${color}; width: 12px; height: 12px; margin-right: 10px; border-radius: 50%;"></span><div class="apexcharts-tooltip-text"><div class="apexcharts-tooltip-y-group"><span class="apexcharts-tooltip-text-y-label">${seriesName}: </span><span class="apexcharts-tooltip-text-y-value">${value}</span></div></div></div></div>`;
          },
        },
      };
    },
  },
  methods: {
    interpolateNulls(dataArray) {
      const result = [...dataArray];
      for (let i = 0; i < result.length; i++) {
        if (result[i] === null) {
          const startIndex = i - 1;
          const startValue = startIndex >= 0 ? result[startIndex] : null;
          if (startValue !== null) {
            let endIndex = -1;
            for (let j = i + 1; j < result.length; j++) {
              if (result[j] !== null) {
                endIndex = j;
                break;
              }
            }
            if (endIndex !== -1) {
              const endValue = result[endIndex];
              const gapCount = endIndex - startIndex;
              const step = (endValue - startValue) / gapCount;
              for (let k = startIndex + 1; k < endIndex; k++) {
                result[k] = Number((startValue + step * (k - startIndex)).toFixed(2));
              }
              i = endIndex - 1;
            }
          }
        }
      }
      return result;
    },
  },
};
</script>
<style scoped>
.counter {
  font-size: 1em;
}

.counter-unit {
  font-size: 0.7em;
  font-weight: 500;
  margin-left: 3px;
}
</style>
